import { useId, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { cx } from '../../lib/cx';

export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  helperText?: ReactNode;
  error?: string;
  containerClassName?: string;
  /** `filled` is the grey field used by the staff Settings forms (Figma A16). */
  variant?: 'outlined' | 'filled';
  /** Right-aligned on the label row (a character counter). */
  labelAside?: ReactNode;
  /** `compact` is the 14px text and 14/12 padding of the lead's note boxes (Figma A05.6, A05.x submit). */
  density?: 'default' | 'compact';
  /** The error replaces the label, in red, instead of sitting under the field (Figma A05.6b "9 of 10 characters minimum"). */
  errorInLabel?: boolean;
}

/** Multiline sibling of Input (same label/helper/focus treatment). */
export function TextArea({
  label,
  helperText,
  error,
  containerClassName,
  className,
  id,
  rows = 4,
  variant = 'outlined',
  labelAside,
  density = 'default',
  errorInLabel = false,
  ...props
}: TextAreaProps) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const messageId = `${fieldId}-message`;
  const inLabel = errorInLabel && !!error;
  const message = inLabel ? undefined : (error ?? helperText);

  return (
    <div className={cx('flex flex-col gap-(--mbm-space-2)', containerClassName)}>
      {label ? (
        <div className="flex items-baseline justify-between gap-(--mbm-space-3)">
          <label
            htmlFor={fieldId}
            role={inLabel ? 'alert' : undefined}
            className={cx(
              'text-label-medium',
              inLabel ? 'text-feedback-error' : variant === 'filled' ? 'text-text-primary' : 'text-text-secondary',
            )}
          >
            {inLabel ? error : label}
          </label>
          {labelAside}
        </div>
      ) : null}
      <textarea
        id={fieldId}
        rows={rows}
        aria-invalid={error ? true : undefined}
        aria-describedby={message ? messageId : undefined}
        className={cx(
          'w-full resize-y border text-text-primary outline-none placeholder:text-text-muted',
          density === 'compact'
            ? 'px-(--mbm-space-3-5) py-(--mbm-space-3) text-body-medium'
            : 'p-(--mbm-space-4) text-body-large',
          variant === 'filled'
            ? 'rounded-(--mbm-radius-sm) bg-surface-tint'
            : 'rounded-(--mbm-component-radius-input) bg-surface-card',
          'focus:outline-2 focus:-outline-offset-2',
          error
            ? 'border-feedback-error focus:outline-feedback-error'
            : cx(variant === 'filled' ? 'border-transparent' : 'border-border-default', 'focus:outline-focus-ring'),
          'disabled:bg-surface-tint disabled:opacity-(--mbm-opacity-disabled)',
          className,
        )}
        {...props}
      />
      {message ? (
        <p
          id={messageId}
          role={error ? 'alert' : undefined}
          className={cx('text-body-small', error ? 'text-feedback-error' : 'text-text-tertiary')}
        >
          {message}
        </p>
      ) : null}
    </div>
  );
}
